import {Nullable} from '../../../app/TypeHelpers'
import {OptionSetting} from '../../facilities/FacilityForReservation'
import {PaymentItem, ReservationOption} from '../ReserveFacilityModel'
import {CHARGE_UNIT} from '../../../CodeMaster'
import {calculateDurations, convertToNullableMoment} from '../helpers/helpers'
import {PaymentItemForViewOption} from '../element/PaymentItemForViewOption'

export class ReserveOptionPaymentCalculator {
    private readonly facilityOptionSettings: Nullable<OptionSetting[]> = null
    private readonly reservationOptions: Nullable<ReservationOption[]> = null
    private readonly startDate: Nullable<string> = null
    private readonly startTime: Nullable<string> = null
    private readonly endDate: Nullable<string> = null
    private readonly endTime: Nullable<string> = null

    constructor(
        facilityOptionSettings: Nullable<OptionSetting[]>,
        reservationOptions: Nullable<ReservationOption[]>,
        startDate: Nullable<string>,
        startTime: Nullable<string>,
        endDate: Nullable<string>,
        endTime: Nullable<string>,
    ) {
        this.facilityOptionSettings = facilityOptionSettings
        this.reservationOptions = reservationOptions
        this.startDate = startDate
        this.startTime = startTime
        this.endDate = endDate
        this.endTime = endTime
    }

    calculate(): PaymentItem[] {
        if (this.facilityOptionSettings === null || this.reservationOptions === null) {
            return []
        }
        const facilityOptionSettings = this.facilityOptionSettings

        return this.reservationOptions
            .map(option => {
                const facilityOption =
                    facilityOptionSettings.find(optionSetting => optionSetting.optionId === option.optionId)
                return this.calculateByChargeUnit(facilityOption, option)
            })
            .filter(option => option !== null) as PaymentItem[]
    }

    private calculateByChargeUnit(
        facilityOption: OptionSetting | undefined, reservationOption: ReservationOption,
    ): Nullable <PaymentItem> {
        if (facilityOption === undefined) {
            return null
        }
        if (facilityOption.chargeUnit === null) {
            return null
        }

        if (facilityOption.chargeUnit === CHARGE_UNIT.FIXED) {
            if (this.startDate === null) {
                throw Error('startDate cannot be null')
            }
            return this.calculatePriceFix(facilityOption, reservationOption.quantity)
        } else {
            const startMoment = convertToNullableMoment(this.startDate, this.startTime)
            const endMoment = convertToNullableMoment(this.endDate, this.endTime)

            if (startMoment === null || endMoment === null) {
                throw Error('startMoment and endMoment cannot be null')
            }
            const {durationInHour, durationInMinute} = calculateDurations(startMoment, endMoment)
            return this.calculatePricePerDuration(
                facilityOption,
                reservationOption.quantity,
                durationInHour,
                durationInMinute,
            )
        }
    }

    private calculatePriceFix(facilityOption: OptionSetting, quantity: number): Nullable<PaymentItem> {
        const charge = facilityOption.charge * quantity

        return new PaymentItemForViewOption(
            facilityOption.optionId,
            facilityOption.name,
            facilityOption.paymentMethod,
            charge,
            quantity,
            facilityOption.unitName,
            facilityOption.sortOrder,
        ).toObject()
    }

    private calculatePricePerDuration(
        facilityOption: OptionSetting,
        quantity: number,
        durationInHour: number,
        durationInMinute: number,
    ): Nullable <PaymentItem> {
        if (facilityOption.chargeUnitTime === null) {
            throw Error('facilityOption.chargeUnitTime cannot be null')
        }
        const duration = (facilityOption.chargeUnit === CHARGE_UNIT.TIME) ? durationInHour : durationInMinute

        return new PaymentItemForViewOption(
            facilityOption.optionId,
            facilityOption.name,
            facilityOption.paymentMethod,
            quantity * facilityOption.charge * Math.ceil(duration / facilityOption.chargeUnitTime),
            quantity,
            facilityOption.unitName,
            facilityOption.sortOrder,
        ).toObject()
    }
}
